import React, {FC, useMemo, useRef, useState} from 'react' 
import * as XLSX from 'xlsx'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import DataTable, {TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import { BaseEntity} from '../../../../shared/models/ApiModels'
import { uploadCars, validateCarUpload } from './Api' 
 
interface ImportValidateError {
  rowNo?: number
  field?: string
  value?: string
  message: string
}

/**
 * Excel'den beklenen kolonlar (başlık -> alan adı).
 * Excel dosyasındaki ilk satırın (header) birebir bu başlıkları
 * içermesi beklenir. Karşılaştırma trim ve lowercase ile yapılır.
 */
const ID_COLUMN_DEFS: Array<{key: string; label: string}> = [
  {key: 'brandId', label: 'Marka ID'},
  {key: 'vehicleTypeId', label: 'Tip ID'},
  {key: 'branchId', label: 'Şube ID'},
  {key: 'locationId', label: 'Lokasyon ID'},
  {key: 'vehicleGroupId', label: 'Grup ID'},
]

const COLUMN_DEFS: Array<{key: string; label: string; required?: boolean, list?: boolean}> = [
  {key: 'plate', label: 'Plaka', required: true},
  {key: 'brand', label: 'Marka', required: true, list: true},
  {key: 'type', label: 'Tipi', required: true, list: true},
  {key: 'detail', label: 'Detay'},
  {key: 'year', label: 'Yılı', required: true},
  {key: 'fuelType', label: 'Yakıt Turu', required: true},
  {key: 'transmission', label: 'Vites', required: true},
  {key: 'branch', label: 'Şube', required: true, list: true},
  {key: 'location', label: 'Lokasyon', list: true},
  {key: 'group', label: 'Grup', required: true, list: true},
  {key: 'sipp', label: 'SIPP'},
  {key: 'chassisNo', label: 'Şasi No'},
  {key: 'lastKm', label: 'Son KM'},
  {key: 'color', label: 'Renk'},
  {key: 'winterTire', label: 'Kar Lastiği'},
  {key: 'documentNo', label: 'Araç Belge No'},
  {key: 'owner', label: 'Araç Sahibi'},
  {key: 'ownerGroup', label: 'Araç Sahip Grup'},
  {key: 'owner2', label: 'Araç Sahibi-2'},
  {key: 'passive', label: 'Pasif'},
  {key: 'trackingNo', label: 'Takip No'},
  {key: 'tapeCode', label: 'Teyp Kodu'},
  {key: 'purchasePrice', label: 'Alış Bedeli'},
  {key: 'hgsNumber', label: 'HGS Numarasi'},
  {key: 'engineNo', label: 'Motor No'},
  {key: 'description', label: 'Açıklama'},
  {key: 'fleetInDate', label: 'Filo Gir. Tar'},
  {key: 'fleetOutDate', label: 'Filo Çık. Tar'},
  {key: 'plannedOutDate', label: 'Çık. Plan. Tarih'},
  {key: 'contractNo', label: 'Sözleşme No'},
  {key: 'service', label: 'Servis'},
  {key: 'baf', label: 'Baf'},
  {key: 'sale', label: 'Satış'},
  {key: 'kaskoValue', label: 'Kasko Bedeli'},
  {key: 'tireWarningKm', label: 'Lastik Uyarı KM'},
  {key: 'firstRegisterDate', label: 'İlk Tescil Tarihi'},
  {key: 'purchaseCompany', label: 'Alınan Firma'},
  {key: 'groupDescription', label: 'Grup Açıklama'},
  {key: 'spareKey', label: 'Yedek Anahtar'},
  {key: 'hgsOgsLicenseDate', label: 'HGS/OGS Ruhsat Tarihi'},
  {key: 'tireInfo', label: 'Lastik Bilgisi'},
  {key: 'secondHandValue', label: '2. El Değeri'},
  {key: 'lastFuel', label: 'Son Yakıt'},
]

export interface CarUploadRow extends BaseEntity {
  id: string
  [key: string]: any
}

const normalizeHeader = (s: any): string =>
  String(s ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('tr-TR')

export const CarUploadPage: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [rows, setRows] = useState<CarUploadRow[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [checking, setChecking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [showIdColumns, setShowIdColumns] = useState(false)

  const columns: TableColumn<CarUploadRow>[] = useMemo(
    () => [
      ...COLUMN_DEFS.map((c) => ({
        key: c.key,
        label: c.label,
        width: '160px',
      })),
      ...(showIdColumns
        ? ID_COLUMN_DEFS.map((c) => ({
            key: c.key,
            label: c.label,
            width: '220px',
          }))
        : []),
    ],
    [showIdColumns]
  )

  const resetState = () => {
    setRows([])
    setErrors([])
    setIsValid(false)
    setShowIdColumns(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null
    resetState()
    if (!selected) {
      setFile(null)
      setFileName('')
      return
    }
    const allowed = ['.xlsx', '.xls', '.csv']
    const lower = selected.name.toLocaleLowerCase()
    if (!allowed.some((ext) => lower.endsWith(ext))) {
      setFile(null)
      setFileName('')
      showErrorToast('Lütfen geçerli bir Excel dosyası seçin (.xlsx, .xls, .csv)')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setFile(selected)
    setFileName(selected.name)
  }

  const readWorkbook = (f: File): Promise<XLSX.WorkBook> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer)
          const wb = XLSX.read(data, {type: 'array', cellDates: true})
          resolve(wb)
        } catch (err) {
          reject(err)
        }
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsArrayBuffer(f)
    })

  const handleCheck = async () => {
    if (!file) {
      showErrorToast('Lütfen önce bir Excel dosyası seçin')
      return
    }
    setChecking(true)
    resetState()
    try {
      const wb = await readWorkbook(file)
      const firstSheet = wb.SheetNames[0]
      if (!firstSheet) {
        showErrorToast("Excel dosyasında sayfa bulunamadı")
        return
      }
      const sheet = wb.Sheets[firstSheet]
      const matrix: any[][] = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
        blankrows: false,
      })
      if (!matrix.length) {
        showErrorToast("Excel dosyası boş")
        return
      }

      const headerRow = matrix[0].map(normalizeHeader)
      const dataRows = matrix.slice(1)

      // Build header index map
      const headerIndex: Record<string, number> = {}
      COLUMN_DEFS.forEach((c) => {
        const idx = headerRow.indexOf(normalizeHeader(c.label))
        headerIndex[c.key] = idx
      })

      const foundErrors: string[] = []
      const missingRequired = COLUMN_DEFS.filter(
        (c) => c.required && headerIndex[c.key] === -1
      )
      if (missingRequired.length > 0) {
        foundErrors.push(
          'Zorunlu kolonlar eksik: ' + missingRequired.map((c) => c.label).join(', ')
        )
      }

      const parsedRows: CarUploadRow[] = []
      dataRows.forEach((raw, i) => {
        // Skip completely empty rows
        const allEmpty = raw.every((v) => v === '' || v === null || v === undefined)
        if (allEmpty) return

        const rowNo = i + 2 // +1 header, +1 1-indexed
        const item: CarUploadRow = {id: `row-${rowNo}`}
        COLUMN_DEFS.forEach((c) => {
          const idx = headerIndex[c.key]
          let value: any = idx >= 0 ? raw[idx] : ''
          if (value instanceof Date) {
            value = value.toLocaleDateString('tr-TR')
          } else if (value !== null && value !== undefined) {
            value = String(value).trim()
          }
          item[c.key] = value
        })

        if (!item.plate) {
          foundErrors.push(`Satır ${rowNo}: Plaka alanı boş olamaz`)
        }
        if (item.year && !/^\d{4}$/.test(String(item.year))) {
          foundErrors.push(`Satır ${rowNo}: Yılı geçerli bir 4 haneli sayı olmalı (${item.year})`)
        }
        parsedRows.push(item)
      })

      if (parsedRows.length === 0 && foundErrors.length === 0) {
        foundErrors.push('Excel dosyasında veri satırı bulunamadı')
      }

      // Duplicate plate check
      const seen = new Set<string>()
      parsedRows.forEach((r, idx) => {
        const p = String(r.plate || '').toLocaleUpperCase('tr-TR')
        if (!p) return
        if (seen.has(p)) {
          foundErrors.push(`Satır ${idx + 2}: Plaka mükerrer (${p})`)
        } else {
          seen.add(p)
        }
      })

      setRows(parsedRows)
      setErrors(foundErrors)
      
      if (foundErrors.length === 0) {
        // Yerel doğrulama başarılı → sadece list=true olan kolonların verisini
        // backend'e gönderip ek doğrulama yaptır.
        const listKeys = COLUMN_DEFS.filter((c) => c.list).map((c) => c.key)
        const payload = parsedRows.map((r, idx) => {
          const item: Record<string, any> = {rowNo: idx + 2}
          listKeys.forEach((k) => {
            item[k] = r[k]
          })
          return item
        })

        try {
          const res = await validateCarUpload(payload as CarUploadRow[])
          const body = res.data
          const serverErrorsRaw: ImportValidateError[] = Array.isArray(body?.payload?.errors)
            ? body.payload.errors
            : []
          const serverCars: any[] = Array.isArray(body?.payload?.cars)
            ? body.payload.cars
            : []

          // Kolon anahtarından kullanıcı dostu başlığa eşleme
          const fieldLabelMap: Record<string, string> = COLUMN_DEFS.reduce((acc, c) => {
            acc[c.key] = c.label
            return acc
          }, {} as Record<string, string>)

          const serverErrors = serverErrorsRaw.map((e) => {
            const parts: string[] = []
            if (e.rowNo != null) parts.push(`Satır ${e.rowNo}`)
            if (e.field) parts.push(fieldLabelMap[e.field] || e.field)
            const prefix = parts.length ? parts.join(' - ') + ': ' : ''
            return `${prefix}${e.message}`
          })

          if (body?.hasError || serverErrors.length > 0) {
            setIsValid(false)
            setErrors(
              serverErrors.length > 0
                ? serverErrors
                : [body?.message || 'Sunucu doğrulaması başarısız']
            )
            showErrorToast('Sunucu doğrulamasında hatalar bulundu')
          } else {
            // rowNo → resolved IDs eşlemesi
            const carMap = new Map<number, any>(serverCars.map((c) => [c.rowNo, c]))
            setRows((prev) =>
              prev.map((r, idx) => {
                const rowNo = idx + 2
                const carData = carMap.get(rowNo)
                if (!carData) return r
                const {rowNo: _rn, ...ids} = carData
                return {...r, ...ids}
              })
            )
            setShowIdColumns(true)
            setIsValid(true)
            showSuccessToast(`${parsedRows.length} satır başarıyla kontrol edildi`)
          }
        } catch (apiErr: any) {
          console.error(apiErr)
          const serverData = apiErr?.response?.data
          const serverErrorsRaw: ImportValidateError[] = Array.isArray(serverData?.payload?.errors)
            ? serverData.payload.errors
            : []
          const fieldLabelMap: Record<string, string> = COLUMN_DEFS.reduce((acc, c) => {
            acc[c.key] = c.label
            return acc
          }, {} as Record<string, string>)
          const serverErrors = serverErrorsRaw.map((e) => {
            const parts: string[] = []
            if (e.rowNo != null) parts.push(`Satır ${e.rowNo}`)
            if (e.field) parts.push(fieldLabelMap[e.field] || e.field)
            const prefix = parts.length ? parts.join(' - ') + ': ' : ''
            return `${prefix}${e.message}`
          })
          setIsValid(false)
          setErrors(
            serverErrors.length > 0
              ? serverErrors
              : [serverData?.message || 'Sunucu doğrulama servisi çağrılamadı']
          )
          showErrorToast('Sunucu doğrulaması sırasında hata oluştu')
        }
      } else {
        setIsValid(false)
        showErrorToast('Excel doğrulamasında hatalar bulundu')
      }
    } catch (err: any) {
      console.error(err)
      showErrorToast('Excel dosyası okunamadı')
    } finally {
      setChecking(false)
    }
  }

  const handleSave = async () => {
    if (!isValid || rows.length === 0) return
    setSaving(true)
    try {
      // TODO: Gerçek API endpoint bağlanacak. Şimdilik yüklenen veriler konsola yazılır.
      // Örn: await uploadCars(rows)
      // eslint-disable-next-line no-console
      console.log('Yüklenecek araçlar:', rows)
      await uploadCars(rows)
      showSuccessToast(`${rows.length} araç başarıyla kaydedildi`)
      // Reset
      setFile(null)
      setFileName('')
      setRows([])
      setErrors([])
      setIsValid(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      console.error(err)
      showErrorToast('Araçlar kaydedilirken bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Araç Excel Yükleme</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-2 my-1'>
              <i className='bi bi-file-earmark-excel fs-1 text-success'></i>
              <h3 className='fw-bold m-0'>Excel ile Araç Yükleme</h3>
            </div>
          </div>
        </div>
        <div className='card-body border-top pt-6'>
          <div className='row g-6 align-items-end mb-6'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>Excel Dosyası (*.xlsx, *.xls, *.csv)</label>
              <input
                ref={fileInputRef}
                type='file'
                accept='.xlsx,.xls,.csv'
                className='form-control'
                onChange={handleFileChange}
                disabled={checking || saving}
              />
              {fileName && (
                <div className='form-text'>
                  Seçilen dosya: <strong>{fileName}</strong>
                </div>
              )}
            </div>
            <div className='col-md-6 d-flex flex-wrap gap-2'>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleCheck}
                disabled={!file || checking || saving}
              >
                {checking ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                    Kontrol Ediliyor...
                  </>
                ) : (
                  <>
                    <i className='bi bi-search me-2'></i>Kontrol Et
                  </>
                )}
              </button>
              <button
                type='button'
                className='btn btn-success'
                onClick={handleSave}
                disabled={!isValid || saving || checking}
              >
                {saving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className='bi bi-check2-circle me-2'></i>Kaydet
                  </>
                )}
              </button>
            </div>
          </div>

          {errors.length > 0 && (
            <div className='alert alert-danger d-flex flex-column' role='alert'>
              <div className='fw-bold mb-2'>
                <i className='bi bi-exclamation-triangle me-2'></i>
                Doğrulama Hataları ({errors.length})
              </div>
              <ul className='mb-0 ps-4' style={{maxHeight: 200, overflow: 'auto'}}>
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {rows.length > 0 && (
            <div className='mt-4'>
              <div className='d-flex align-items-center justify-content-between mb-3'>
                <h5 className='fw-bold m-0'>
                  Önizleme
                  <span className='badge badge-light-primary ms-2'>{rows.length} satır</span>
                </h5>
                {isValid && (
                  <span className='badge badge-light-success'>
                    <i className='bi bi-check-circle me-1'></i> Kayda Hazır
                  </span>
                )}
              </div>
              <DataTable
                data={rows}
                columns={columns}
                showPagination={false}
                striped
                bordered
                hover
              />
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </>
  )
}

export default CarUploadPage
